import React, { useCallback, useEffect, useState } from 'react';

import ListView from 'components/Crud/views/List';
import { reserveCapacityDomain } from 'models';
import {
  makeStyles,
  TableCell,
  TableRow,
  IconButton,
  MuiThemeProvider
} from '@material-ui/core';
import Typography from 'components/Typography';
import CustomButton from 'components/Button';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { useDispatch, useSelector } from 'react-redux';
import {
  useTableStyles,
  useTableRowActionsState,
  Column,
  ListCapacityTable,
  TableActions
} from 'views/CapacityTypes/ListCapacityTable';
import { fetchGeographicAreas } from 'store/slices/geographicAreas';
import { fetchCapacityTypes } from 'store/slices/capacityTypes';
import { reserveCapacitySelector } from 'store/slices/reserveCapacity';
import { DateTime } from 'luxon';
import { DATE_WITH_SLASH_FORMAT } from 'constants/dates';
import { DayOfWeek } from 'models/Adjustment';
import {
  useCapacityTypesState,
  useGeographicAreaState,
  useReservedCapacityData
} from 'store/selectors';
import ReserveCapacity, { ReserveCapacityStatus } from 'models/ReserveCapacity';
import DayChip from './DayChip';
import reserveCapacityTheme from './reserveCapacityTheme';
import EditReserveCapacityDrawer from './EditReserveCapacityDrawer';
import AddReservedCapacityDrawer from './AddReservedCapacityDrawer';

const useStyles = makeStyles(
  theme => ({
    title: {
      marginBottom: theme.spacing(1)
    },
    pageHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      paddingBottom: theme.spacing(10)
    },
    addCapacityTypeButton: {
      padding: theme.spacing(1),
      alignSelf: 'flex-end',
      textTransform: 'capitalize'
    },
    listViewContainer: {
      paddingTop: theme.spacing(5),
      // Material UI adds an override for `fontSize="variant"` on button icons, which sets the font size to 22...
      '& [class*="MuiButton-iconSizeLarge"] > *:first-child': {
        fontSize: theme.spacing(3)
      }
    },
    statusLabel: {
      '&.active': {
        borderColor: theme.palette.aegis.semantic.primary.base,
        backgroundColor: theme.palette.aegis.semantic.brand?.bright,
        color: theme.palette.aegis.semantic.primary.base
      },
      '&.next': {
        borderColor: theme.palette.aegis.semantic.warning.base,
        backgroundColor: theme.palette.aegis.semantic.warning.muted,
        color: theme.palette.aegis.semantic.warning.base
      },
      '&.ended': {
        borderColor: theme.palette.aegis.semantic.info.base,
        backgroundColor: theme.palette.aegis.semantic.info.muted,
        color: theme.palette.aegis.semantic.info.alt
      },
      display: 'inline-block',
      padding: theme.spacing(0.5, 1),
      fontSize: 14,
      lineHeight: '14px',
      borderWidth: 1,
      borderStyle: 'solid',
      borderRadius: theme.spacing(0.5),
      textTransform: 'capitalize'
    },
    dayChipsWrapper: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8
    },
    highlightedRow: {
      '&&&': {
        backgroundColor: `${theme.palette.aegis.colour.green.lightest}`
      }
    }
  }),
  { name: 'ReserveCapacityView' }
);

const columnsWidth = [105, 150, 169, 229, 129, 151, 123, 52];

const tableHeaders: Column[] = [
  {
    label: 'Status',
    renderButton: (className: string) => (
      <IconButton
        className={className}
        onClick={() => null}
        data-testid="reserveCapacityFilterArea"
      />
    )
  },
  {
    label: 'Areas',
    renderButton: (className: string) => (
      <IconButton
        className={className}
        onClick={() => null}
        data-testid="reserveCapacityFilterArea"
      />
    )
  },
  {
    label: 'Capacity Type',
    renderButton: (className: string) => (
      <IconButton
        className={className}
        onClick={() => null}
        data-testid="reserveCapacityFilterArea"
      />
    )
  },
  {
    label: 'Valid Between',
    renderButton: (className: string) => (
      <IconButton
        className={className}
        onClick={() => null}
        data-testid="reserveCapacityFilterArea"
      />
    )
  },
  {
    label: 'Capacity',
    renderButton: (className: string) => (
      <IconButton
        className={className}
        onClick={() => null}
        data-testid="reserveCapacityFilterArea"
      />
    )
  },
  {
    label: 'Release At',
    renderButton: (className: string) => (
      <IconButton
        className={className}
        onClick={() => null}
        data-testid="reserveCapacityFilterArea"
      />
    )
  },
  {
    label: 'Days',
    renderButton: (className: string) => (
      <IconButton
        className={className}
        onClick={() => null}
        data-testid="reserveCapacityFilterArea"
      />
    )
  }
];

function ListReserveCapacityView() {
  const tableClasses = useTableStyles({ containerSize: 'max' });
  const classes = useStyles();

  const dispatch = useDispatch();
  const { rowHovered, setRowHovered } = useTableRowActionsState();

  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const [createdId, setCreatedId] = useState<string | undefined>();

  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [idToEdit, setIdToEdit] = useState<string | undefined>();
  const [editedId, setEditedId] = useState<string | undefined>();

  useEffect(() => {
    if (createdId) {
      const timer = setTimeout(() => setCreatedId(undefined), 6000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [createdId]);

  useEffect(() => {
    dispatch(fetchGeographicAreas());
    dispatch(fetchCapacityTypes(true));
  }, []);

  const rawReserveCapacityData = useReservedCapacityData();
  const mappedReserveCapacity = useSelector(reserveCapacitySelector);
  const { isLoading: capacityTypesLoading } = useCapacityTypesState();
  const { isLoading: geographicAreasLoading } = useGeographicAreaState();

  const dayMap = new Map();

  Object.values(DayOfWeek).forEach(day => {
    const [firstLetter] = Array.from(day);

    if (!Array.from(dayMap.values()).includes(firstLetter)) {
      dayMap.set(day, firstLetter);
    } else {
      dayMap.set(day, day.substring(0, 2));
    }
  });

  const getRawDomainItem = useCallback(
    (domainItemId: string) =>
      rawReserveCapacityData.find(
        ({ id }) => domainItemId === id
      ) as ReserveCapacity,
    [rawReserveCapacityData]
  );

  const toggleAddDrawer = () => {
    setIsAddDrawerOpen(state => !state);
  };

  const toggleEditDrawer = () => {
    setIsEditDrawerOpen(state => !state);
  };

  useEffect(() => {
    if (editedId) {
      const timer = setTimeout(() => setEditedId(undefined), 6000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [editedId]);

  const isStatusEnded = (status: ReserveCapacityStatus) =>
    status === ReserveCapacityStatus.Ended;

  return (
    <MuiThemeProvider theme={reserveCapacityTheme}>
      <ListView
        domain={reserveCapacityDomain}
        className={classes.listViewContainer}
        loadingDependency={[capacityTypesLoading, geographicAreasLoading].some(
          Boolean
        )}
        disableErrorDisplay
      >
        {({ onDelete }) => (
          <>
            <header className={classes.pageHeader}>
              <div>
                <Typography
                  variant="h1"
                  className={classes.title}
                  data-testid="reserveCapacityPageTitle"
                >
                  Reserve capacity
                </Typography>
                <Typography
                  variant="body"
                  component="p"
                  data-testid="reserveCapacityPageSubtitle"
                >
                  Manage Capacity across different teams and project types
                </Typography>
              </div>
              <CustomButton
                color="primary"
                className={`${classes.addCapacityTypeButton}`}
                data-testid={`${reserveCapacityDomain.type}TableButton`}
                startIcon={<AddIcon fontSize="inherit" />}
                onClick={toggleAddDrawer}
              >
                Add capacity
              </CustomButton>
            </header>
            <section className={tableClasses.tableContainer}>
              <ListCapacityTable
                columnsWidth={columnsWidth}
                label={`A table of ${reserveCapacityDomain.label}`}
                columnHeaders={tableHeaders}
                domain={reserveCapacityDomain}
                rowHovered={rowHovered}
                setRowHovered={setRowHovered}
                noResultsActionLabel="add capacity"
              >
                {mappedReserveCapacity.map(reservedCapacity => (
                  <TableRow
                    key={reservedCapacity.id}
                    data-testid={`tableRow-${reservedCapacity.id}`}
                    className={`${tableClasses.tableRow} ${
                      editedId === reservedCapacity.id ||
                      createdId === reservedCapacity.id
                        ? classes.highlightedRow
                        : ''
                    }`}
                    hover
                  >
                    <TableCell className={tableClasses.tableCell}>
                      <div
                        className={`${
                          classes.statusLabel
                        } ${reservedCapacity.status.toLowerCase()}`}
                      >
                        {reservedCapacity.status.toLowerCase()}
                      </div>
                    </TableCell>
                    <TableCell className={tableClasses.tableCell}>
                      <div className={tableClasses.twoLineTruncate}>
                        {reservedCapacity.area}
                      </div>
                    </TableCell>
                    <TableCell className={tableClasses.tableCell}>
                      <div className={tableClasses.twoLineTruncate}>
                        {reservedCapacity.capacityType}
                      </div>
                    </TableCell>
                    <TableCell className={tableClasses.tableCell}>
                      {DateTime.fromISO(reservedCapacity.startDate).toFormat(
                        DATE_WITH_SLASH_FORMAT
                      )}{' '}
                      &ndash;{' '}
                      {DateTime.fromISO(reservedCapacity.endDate).toFormat(
                        DATE_WITH_SLASH_FORMAT
                      )}
                    </TableCell>
                    <TableCell className={tableClasses.tableCell}>
                      {reservedCapacity.percentage}%
                    </TableCell>
                    <TableCell className={tableClasses.tableCell}>
                      {reservedCapacity.releaseAt} Days
                    </TableCell>
                    <TableCell className={tableClasses.tableCell}>
                      <div className={tableClasses.twoLineTruncate}>
                        <div className={classes.dayChipsWrapper}>
                          {reservedCapacity.days.map(day => (
                            <DayChip key={day} label={dayMap.get(day)} />
                          ))}
                        </div>
                      </div>
                    </TableCell>
                    {!isStatusEnded(reservedCapacity.status) ? (
                      <TableActions
                        menuItems={[
                          {
                            label: 'Edit',
                            icon: <EditIcon />,
                            onClick: () => () => {
                              setIdToEdit(reservedCapacity.id);
                              toggleEditDrawer();
                            }
                          },
                          {
                            label: 'Delete',
                            icon: <DeleteIcon />,
                            onClick: onDelete
                          }
                        ]}
                        domainItem={getRawDomainItem(reservedCapacity.id)}
                      />
                    ) : (
                      <TableCell
                        className={`${tableClasses.tableCell} ${tableClasses.actionsButtonCell}`}
                      />
                    )}
                  </TableRow>
                ))}
              </ListCapacityTable>
              <EditReserveCapacityDrawer
                isOpen={isEditDrawerOpen}
                toggleDrawer={toggleEditDrawer}
                reserveCapacityId={idToEdit as string}
                setEditedId={setEditedId}
              />
              <AddReservedCapacityDrawer
                isOpen={isAddDrawerOpen}
                toggleDrawer={toggleAddDrawer}
                setCreatedId={setCreatedId}
              />
            </section>
          </>
        )}
      </ListView>
    </MuiThemeProvider>
  );
}

export default ListReserveCapacityView;
